/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import {
  Box,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';

import { PluginComponent } from 'plugins';
import SaveButton from 'components/CustomButtons/SaveButton';
import CloseButton from 'components/CustomButtons/CloseButton';
import { previousModalPage } from 'actions/modalPage';
import Loader from 'components/Loader';
import {
  getDocSelected,
  getInvoiceFullyReturned,
  getIsLoading,
  getModalTitle,
  getOrdersSelected,
  getReturnDocument,
  getSalesDocs,
} from 'reducers/returnProducts';
import {
  addSelectedOrdersToShoppingCart,
  fetchSalesDocs,
  setLoading,
  setOrdersSelected,
} from 'actions/returnProducts';
import { useAsyncFunctionNoParallelExecution } from 'utils/hooks/useSingleUseFunction';

import './ProductReturn.scss';

import { DocumentSelection, ReturnTable } from './components';
import { useOrderCancellation } from './hooks';

const ReturnProductContent = () => {
  const invoiceFullyReturned = useSelector(getInvoiceFullyReturned);
  const salesDocs = useSelector(getSalesDocs);
  const returnDocument = useSelector(getReturnDocument);

  useOrderCancellation();

  if (salesDocs.length > 1 && !returnDocument) {
    return <DocumentSelection />;
  }
  if (!returnDocument) return null;

  if (
    returnDocument.type === 'PREPAYMENT' ||
    returnDocument.type === 'ORDER' ||
    invoiceFullyReturned
  ) {
    return null;
  }

  return (
    <PluginComponent hookname="UIReturnTable">
      <ReturnTable />
    </PluginComponent>
  );
};

type ProductReturnProps = {
  saleNumberProp?: string;
};

const ProductReturn: FC<ProductReturnProps> = ({ saleNumberProp }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('return');

  const docSelected = useSelector(getDocSelected);
  const ordersSelected: Record<string, any> = useSelector(getOrdersSelected);
  const modalTitle = useSelector(getModalTitle);
  const loading = useSelector(getIsLoading);

  const [salesDocNumber, setSalesDocNumber] = useState(saleNumberProp ?? '');

  const [ordersSelectedInitVal, setOrdersSelectedInitVal] = useState<Record<
    string,
    any
  > | null>(null);
  useEffect(() => {
    if (!ordersSelectedInitVal && ordersSelected) {
      setOrdersSelectedInitVal(ordersSelected);
    }
  }, [ordersSelectedInitVal, ordersSelected]);

  const getSales = useCallback(
    async event => {
      if (event) {
        event.preventDefault();
      }
      dispatch(setLoading(true));
      salesDocNumber &&
        (await dispatch(
          fetchSalesDocs({
            number: salesDocNumber,
            search_invoice_by_regular_and_custom_number: 1,
            getReturnedPayments: 1,
          }),
        ));
      dispatch(setLoading(false));
    },
    [dispatch, salesDocNumber],
  );

  // Do a search whenever the number is changed
  useDebounce(() => getSales(null), 500, [getSales]);

  const close = () => {
    // update the ordersSelected state to initial value from when the component was opened
    dispatch(setOrdersSelected(ordersSelectedInitVal));
    dispatch(previousModalPage());
  };

  const onInvoiceNumberChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSalesDocNumber(
      event.target.value.replace(/k$/, 'K').replace(/([kK](?=.))/g, ''),
    );
  const loadingText = t('alerts.loading');

  const invalidAmounts = Object.values(ordersSelected).some(
    order =>
      Number(order.amount) > order.originalAmount || Number(order.amount) === 0,
  );

  const [startReturn, {isUsed: returnStarted}] = useAsyncFunctionNoParallelExecution(() => dispatch(addSelectedOrdersToShoppingCart()))
  return (
    <div className="product-return" data-testid="product-return">
      <Box
        display="flex"
        alignItems="center"
        padding="1rem"
        justifyContent="space-between"
      >
        <PluginComponent hookname="UIExchangeModalTitle">
          <Typography variant="h5" data-testid="return-title">
            {modalTitle}
          </Typography>
        </PluginComponent>
        <Box display="flex" alignItems="center">
          <SaveButton
            data-testid="save-return-btn"
            title={
              docSelected
                ? t('common:confirm')
                : t('common:save', { context: 'changes' })
            }
            action={startReturn}
            disabled={invalidAmounts || returnStarted}
          />
          <PluginComponent hookname="UICustomCloseButton">
            <CloseButton action={close} />
          </PluginComponent>
        </Box>
      </Box>
      <Divider />
      <Box padding="1rem">
        <form onSubmit={getSales}>
          <TextField
            variant="outlined"
            fullWidth
            onChange={onInvoiceNumberChange}
            onFocus={event => {
              event.preventDefault();
            }}
            value={salesDocNumber}
            label={t('fields.invoiceNumber', {
              context: 'placeholder',
            })}
            className="invoice-number"
            autoFocus
            InputProps={{
              inputProps: {
                'data-testid': 'invoice-number',
              },
              endAdornment: (
                <InputAdornment position="end">
                  <Divider
                    orientation="vertical"
                    style={{ width: 2, height: 35, marginRight: '1rem' }}
                  />
                  <SearchOutlinedIcon
                    onClick={getSales}
                    className="search-button"
                  />
                </InputAdornment>
              ),
            }}
          />
        </form>
        <Loader withPadding loadingText={loadingText} block show={loading}>
          <ReturnProductContent />
        </Loader>
      </Box>
    </div>
  );
};

export default ProductReturn;
